import { useState } from 'react';
import { T, useGT } from 'gt-react';
import { WarningIcon } from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  useStageSelectedChanges,
  useStageAllChanges,
  useStageFromItemList,
} from '@/api/rpc/hooks/inventory-management/useMetadataChangeSets';
import type {
  CatalogAdvancedFilter,
  CatalogFilterMatch,
  CatalogRecordKeyScope,
  StageChangesResponse,
} from '@erp/rpc';
import { ChangeSetNameFields } from './ChangeSetNameFields';
import { useStagedChangeSetFeedback } from './useStagedChangeSetFeedback';

export type StageMode = 'selected' | 'all';

// Above this many products the modal warns before staging.
export const STAGE_WARN_THRESHOLD = 10000;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: StageMode;
  /** Keys of the explicitly selected products (used only in 'selected' mode). */
  productKeys: string[];
  /** Current catalog search + filters (used only in 'all' mode). */
  search?: string;
  searchMode: 'text' | 'regex';
  advancedFilters: CatalogAdvancedFilter[];
  /**
   * How those filters are matched — part of "matching" for the same reason the
   * key scope is: read row-by-row and product-wide, the same constraints select
   * different products, so staging under the other reading would stage a set
   * the reader never saw.
   */
  advancedFilterMatch: CatalogFilterMatch;
  /**
   * The catalog's Source / Location scope ('all' mode only). Part of what
   * "matching" means: staging without it would stage records from divisions and
   * locations the reader had filtered out.
   */
  keyScope: CatalogRecordKeyScope;
  /**
   * When the multiple-items filter is active: the resolved product keys of the
   * whole matching set. 'all' mode then stages exactly these via
   * `stageSelected` — the search/filters alone can't reproduce a keyed view,
   * and `stageAll` doesn't take keys.
   */
  itemFilterKeys?: string[];
  /**
   * When the item-list filter is the catalog's only scope: the active list's
   * id. 'all' mode then stages the whole list server-side via
   * `stageFromItemList` — the search/filters alone can't reproduce a
   * list-scoped view, and `stageAll` doesn't take a list. (The caller keeps
   * "Stage all" unavailable while the list is combined with other filters.)
   */
  itemListId?: string;
  /**
   * Total products matching the current filters. Undefined while the async
   * catalog count is still loading — staging stays allowed (the backend
   * resolves the same filters itself); only the display and the size warning
   * wait for it.
   */
  totalCount: number | undefined;
  /** Called after a successful stage so the parent can clear its selection. */
  onStaged: () => void;
}

// ─── Pure helpers (no hooks/gt — kept small and independently testable) ───────

/** The number of products the current action would stage (unknown = undefined). */
function stageCount(
  mode: StageMode,
  selectedCount: number,
  totalCount: number | undefined,
): number | undefined {
  return mode === 'selected' ? selectedCount : totalCount;
}

/** Submit is allowed once a name is entered and there is something to stage
 * (an unknown count doesn't block — the backend stages whatever matches). */
function canStage(
  name: string,
  isPending: boolean,
  count: number | undefined,
): boolean {
  return name.trim().length > 0 && !isPending && count !== 0;
}

interface StartStageArgs {
  mode: StageMode;
  name: string;
  description: string;
  productKeys: string[];
  search?: string;
  searchMode: 'text' | 'regex';
  advancedFilters: CatalogAdvancedFilter[];
  advancedFilterMatch: CatalogFilterMatch;
  keyScope: CatalogRecordKeyScope;
  itemFilterKeys?: string[];
  itemListId?: string;
  stageSelected: ReturnType<typeof useStageSelectedChanges>;
  stageAll: ReturnType<typeof useStageAllChanges>;
  stageFromList: ReturnType<typeof useStageFromItemList>;
  callbacks: {
    onSuccess: (res: StageChangesResponse) => void;
    onError: (error: Error) => void;
  };
}

/** Fire the right staging mutation for the mode with a trimmed name/description. */
function startStage(args: StartStageArgs): void {
  const base = {
    name: args.name.trim(),
    description: args.description.trim() || undefined,
  };
  if (args.mode === 'selected') {
    args.stageSelected.mutate(
      { ...base, productKeys: args.productKeys },
      args.callbacks,
    );
  } else if (args.itemFilterKeys) {
    // "All matching" under the multiple-items filter: the client already holds
    // every matching key (that's what the filter is), so stage exactly those.
    args.stageSelected.mutate(
      { ...base, productKeys: args.itemFilterKeys },
      args.callbacks,
    );
  } else if (args.itemListId) {
    // "All matching" under the item-list filter: the list's keys live
    // server-side, so stage the list itself — no keys transit the client.
    args.stageFromList.mutate(
      { ...base, itemListId: args.itemListId },
      args.callbacks,
    );
  } else {
    args.stageAll.mutate(
      {
        ...base,
        search: args.search,
        searchMode: args.searchMode,
        advancedFilters: args.advancedFilters.length
          ? args.advancedFilters
          : undefined,
        advancedFilterMatch: args.advancedFilterMatch,
        ...args.keyScope,
      },
      args.callbacks,
    );
  }
}

// ─── Orchestration hook ───────────────────────────────────────────────────────

function useStageChangeSet(props: Props) {
  const stageSelected = useStageSelectedChanges();
  const stageAll = useStageAllChanges();
  const stageFromList = useStageFromItemList();
  const feedback = useStagedChangeSetFeedback({
    landOn: 'reviews',
    onDone: () => {
      props.onStaged();
      props.onOpenChange(false);
    },
  });

  // 'all' under the multiple-items filter stages the resolved keys — their
  // count is exact and known client-side (0 while the resolution is still in
  // flight, which keeps the submit closed rather than staging everything).
  const count =
    props.mode === 'all' && props.itemFilterKeys
      ? props.itemFilterKeys.length
      : stageCount(props.mode, props.productKeys.length, props.totalCount);
  const isPending =
    stageSelected.isPending || stageAll.isPending || stageFromList.isPending;
  const exceedsThreshold = count !== undefined && count > STAGE_WARN_THRESHOLD;

  const submit = (name: string, description: string) => {
    startStage({
      mode: props.mode,
      name,
      description,
      productKeys: props.productKeys,
      search: props.search,
      searchMode: props.searchMode,
      advancedFilters: props.advancedFilters,
      advancedFilterMatch: props.advancedFilterMatch,
      keyScope: props.keyScope,
      itemFilterKeys: props.itemFilterKeys,
      itemListId: props.itemListId,
      stageSelected,
      stageAll,
      stageFromList,
      callbacks: feedback,
    });
  };

  return { isPending, exceedsThreshold, count, submit };
}

// ─── Presentational pieces ────────────────────────────────────────────────────

function StageTitle({ mode }: { mode: StageMode }) {
  return mode === 'selected' ? (
    <T context="stage change set modal title, selected products">
      Stage selected products
    </T>
  ) : (
    <T context="stage change set modal title, all matching products">
      Stage all matching products
    </T>
  );
}

export function StageOverflowWarning() {
  return (
    <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
      <WarningIcon className="mt-0.5 h-4 w-4 shrink-0" />
      <span>
        <T context="warning when staging would add over 10000 products to a change set">
          This will stage over 10,000 products. This can take a while and
          creates a large change set.
        </T>
      </span>
    </div>
  );
}

function StageSubmitLabel({
  isPending,
  exceedsThreshold,
}: {
  isPending: boolean;
  exceedsThreshold: boolean;
}) {
  if (isPending) {
    return (
      <T context="submit button label while staging is in progress">Staging…</T>
    );
  }
  if (exceedsThreshold) {
    return (
      <T context="submit button label to confirm staging over 10000 products">
        Confirm & stage
      </T>
    );
  }
  return <T context="submit button label in stage change set modal">Stage</T>;
}

// ─── Modal + form ─────────────────────────────────────────────────────────────

export function StageChangeSetModal(props: Props) {
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent className="sm:max-w-md">
        {/* Mount the form only while open so its fields reset on every open. */}
        {props.open && <StageChangeSetForm {...props} />}
      </DialogContent>
    </Dialog>
  );
}

function StageChangeSetForm(props: Props) {
  const gt = useGT();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const { isPending, exceedsThreshold, count, submit } =
    useStageChangeSet(props);
  const canSubmit = canStage(name, isPending, count);

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          <StageTitle mode={props.mode} />
        </DialogTitle>
        <DialogDescription>
          {gt(
            '{count} products will be staged as a new change set for review.',
            {
              context:
                'stage change set modal subtitle showing the product count',
              count:
                count === undefined
                  ? gt('…', {
                      context:
                        'placeholder while the stage-all product count loads',
                    })
                  : count.toLocaleString(),
            },
          )}
        </DialogDescription>
      </DialogHeader>

      <div className="flex flex-col gap-4 py-2">
        {exceedsThreshold && <StageOverflowWarning />}

        <ChangeSetNameFields
          idPrefix="change-set"
          name={name}
          onNameChange={setName}
          description={description}
          onDescriptionChange={setDescription}
          disabled={isPending}
        />
      </div>

      <DialogFooter>
        <Button
          variant="outline"
          onClick={() => props.onOpenChange(false)}
          disabled={isPending}
        >
          <T context="cancel button in stage change set modal">Cancel</T>
        </Button>
        <Button
          onClick={() => canSubmit && submit(name, description)}
          disabled={!canSubmit}
        >
          <StageSubmitLabel
            isPending={isPending}
            exceedsThreshold={exceedsThreshold}
          />
        </Button>
      </DialogFooter>
    </>
  );
}
